import { useEffect, useRef, useState } from "react";
import {
  useCreateInvite,
  useProjectMembers,
  useRemoveMember,
  useRevokeInvite,
  type ProjectInviteSummary,
  type ProjectMemberSummary,
} from "@vorloop/api-client-react";

/**
 * Invite modal — now backed by real project_members + project_invites
 * tables. The Invite button on the top bar opens this; the owner can
 * add collaborators by email, see pending invites with copyable links,
 * and remove members.
 */
export interface InviteModalProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onClose: () => void;
}

export function InviteModal({ projectId, projectName, open, onClose }: InviteModalProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const members = useProjectMembers(projectId, { enabled: open });
  const createInvite = useCreateInvite(projectId, {
    onSuccess: () => setEmail(""),
  });
  const revokeInvite = useRevokeInvite(projectId);
  const removeMember = useRemoveMember(projectId);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!copiedToken) return;
    const t = setTimeout(() => setCopiedToken(null), 1500);
    return () => clearTimeout(t);
  }, [copiedToken]);

  if (!open) return null;

  function inviteLink(token: string): string {
    return `${typeof window !== "undefined" ? window.location.origin : ""}/accept/${token}`;
  }

  async function copyInvite(token: string) {
    const url = inviteLink(token);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedToken(token);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) return;
    createInvite.mutate({ email: trimmed, role });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Invite collaborators"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(2px)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
    >
      <div
        ref={ref}
        style={{
          width: "min(520px, 92vw)",
          maxHeight: "min(640px, 92vh)",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-strong)",
          borderRadius: 14,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 18,
          color: "var(--text-primary)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
          overflow: "hidden",
        }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Invite to {projectName}</h2>
            <p style={{ margin: "4px 0 0", color: "var(--text-tertiary)", fontSize: 12 }}>
              Add collaborators by email or share a link.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              cursor: "pointer",
              padding: 4,
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </header>

        <form onSubmit={submit} style={{ display: "flex", gap: 8 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="collaborator@example.com"
            style={inputStyle}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "editor" | "viewer")}
            style={selectStyle}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            type="submit"
            disabled={createInvite.isPending || !email.trim() || !email.includes("@")}
            style={{
              ...primaryBtnStyle,
              background: createInvite.isPending ? "var(--bg-active)" : "var(--accent-blue)",
              cursor: createInvite.isPending ? "wait" : "pointer",
            }}
          >
            {createInvite.isPending ? "Inviting…" : "Invite"}
          </button>
        </form>

        {createInvite.error && (
          <div style={errorBoxStyle}>{createInvite.error.message}</div>
        )}

        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <Section title="Collaborators">
            {members.isLoading && <SubText>Loading…</SubText>}
            {members.data?.owner && (
              <PersonRow
                handle={members.data.owner.handle}
                email={members.data.owner.email}
                badge="Owner"
                muted
              />
            )}
            {members.data?.members.map((m: ProjectMemberSummary) => (
              <PersonRow
                key={m.userId}
                handle={m.handle}
                email={m.email}
                badge={m.role}
                onRemove={() => removeMember.mutate({ userId: m.userId })}
              />
            ))}
            {!members.isLoading &&
              (members.data?.members.length ?? 0) === 0 && (
                <SubText>No collaborators yet.</SubText>
              )}
          </Section>

          <Section title="Pending invites">
            {(members.data?.invites.length ?? 0) === 0 && (
              <SubText>No pending invites.</SubText>
            )}
            {members.data?.invites.map((inv: ProjectInviteSummary) => (
              <InviteRow
                key={inv.id}
                invite={inv}
                copied={copiedToken === inv.token}
                onCopy={() => copyInvite(inv.token)}
                onRevoke={() => revokeInvite.mutate({ inviteId: inv.id })}
              />
            ))}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <h3 style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--text-tertiary)" }}>
        {title}
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </section>
  );
}

function SubText({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "var(--text-tertiary)", fontSize: 12, padding: "4px 2px" }}>{children}</div>;
}

function PersonRow({
  handle,
  email,
  badge,
  muted,
  onRemove,
}: {
  handle: string;
  email: string;
  badge: string;
  muted?: boolean;
  onRemove?: () => void;
}) {
  const initial = handle?.[0]?.toUpperCase() ?? "?";
  return (
    <div style={rowStyle}>
      <span style={avatarStyle}>{initial}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: muted ? "var(--text-secondary)" : "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {handle}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {email}
        </div>
      </div>
      <span style={badgePill(badge.toLowerCase())}>{badge}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} style={smallGhostBtn}>
          Remove
        </button>
      )}
    </div>
  );
}

function InviteRow({
  invite,
  copied,
  onCopy,
  onRevoke,
}: {
  invite: ProjectInviteSummary;
  copied: boolean;
  onCopy: () => void;
  onRevoke: () => void;
}) {
  return (
    <div style={rowStyle}>
      <span style={{ ...avatarStyle, background: "var(--bg-active)" }}>✉</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {invite.email}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
          {invite.role} · invited {relativeTime(invite.createdAt)}
        </div>
      </div>
      <button type="button" onClick={onCopy} style={smallGhostBtn}>
        {copied ? "Copied" : "Copy link"}
      </button>
      <button type="button" onClick={onRevoke} style={smallGhostBtn}>
        Revoke
      </button>
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: "8px 10px",
  background: "var(--bg-canvas)",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
};

const selectStyle: React.CSSProperties = {
  padding: "8px 10px",
  background: "var(--bg-canvas)",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  cursor: "pointer",
};

const primaryBtnStyle: React.CSSProperties = {
  padding: "8px 16px",
  background: "var(--accent-blue)",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};

const errorBoxStyle: React.CSSProperties = {
  padding: 10,
  background: "rgba(239,68,68,0.08)",
  border: "1px solid rgba(239,68,68,0.4)",
  borderRadius: 8,
  color: "var(--accent-red)",
  fontSize: 12,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: 8,
  background: "var(--bg-panel-2)",
  border: "1px solid var(--border-default)",
};

const avatarStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 8,
  background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: 12,
  fontWeight: 700,
  flexShrink: 0,
};

const smallGhostBtn: React.CSSProperties = {
  padding: "4px 10px",
  background: "transparent",
  color: "var(--text-secondary)",
  border: "1px solid var(--border-default)",
  borderRadius: 6,
  fontSize: 11,
  cursor: "pointer",
  flexShrink: 0,
};

function badgePill(kind: string): React.CSSProperties {
  const colors: Record<string, { bg: string; fg: string }> = {
    owner: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24" },
    editor: { bg: "rgba(99,102,241,0.12)", fg: "#a5b4fc" },
    viewer: { bg: "rgba(107,114,128,0.18)", fg: "#cbd5e1" },
  };
  const c = colors[kind] ?? colors["viewer"]!;
  return {
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    background: c.bg,
    color: c.fg,
    flexShrink: 0,
  };
}
